import React, { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { Search } from "lucide-react";

import Button from "@/components/common/Button";
import FormField from "@/components/common/FormField";
import { MainHeading } from "@/components/common/MainHeading";
import { Layout } from "@/components/Layout/Layout";
import { callApi, ThrowError } from "@/api/callApi";
import { API_ENDPOINT, API_URL } from "@/api/constant";
import { formatRand, showToastNotification, GetFilterUrl } from "@/utility/helper";
import { errorTypes } from "@/utility/validation";
import { IAddAddonForm, ISingleAddonResponse } from "@/interfaces/Addon/addon.interface";
import { IProduct, IProductResponse } from "@/interfaces/Product/product.interface";
import CommonTable, { TableColumn } from "@/components/common/CommonTable";
import Pagination from "@/components/common/Pagination";
import { useFilter } from "@/hooks/useFilter";
import { IFilter, IPageInfo } from "@/interfaces/common.interface";
import { Switch } from "@/components/ui/switch";
import { StatusBadge } from "@/components/common/StatusBadge";
import { useActiveRole } from "@/hooks/useActiveRole";
import { RootState, useAppSelector } from "@/redux/store";
import { usePermissions } from "@/hooks/usePermissions";

export const AddAddOns = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const isEditMode = Boolean(id);

    const [loadingDetails, setLoadingDetails] = useState(false);
    const [products, setProducts] = useState<IProduct[]>([]);
    const [loadingProducts, setLoadingProducts] = useState(false);
    const [pageMetadata, setPageMetadata] = useState<IPageInfo>({
        limit: 5,
        page: 1,
        total: 0,
        totalPages: 0,
    });
    const user = useAppSelector((state: RootState) => state.user.user);
    const { isAdmin } = usePermissions();
    const { filters, debouncedFilters, updateFilter, handlePageChange } = useFilter<IFilter>({
        search: '',
        page: 1,
        limit: 5,
        ...(isAdmin ? { isGlobal: "1" } : { isGlobal: "0" }),
    });

    const {
        register,
        control,
        handleSubmit,
        watch,
        setValue,
        reset,
        formState: { errors, isSubmitting },
    } = useForm<IAddAddonForm>({
        defaultValues: {
            status: 'active',
            productId: '',
            name: '',
            price: 0,
            isCounter: false,
            inventory: 0,
        }
    });

    const selectedProductId = watch("productId");


    const fetchProducts = async () => {
        setLoadingProducts(true);
        try {
            const queryParams = GetFilterUrl(debouncedFilters);
            let url = `${API_URL + API_ENDPOINT.products}?${queryParams}`;

            if (!isAdmin && user?.id) {
                url += `&userId=${user.id}`;
            }

            const response = await callApi<IProductResponse>(url, { method: 'GET' });
            setProducts(response?.data?.products || []);
            setPageMetadata(
                response?.data?.pageInfo || { limit: 5, page: 1, total: 0, totalPages: 0 }
            );
        } catch (error) {
            console.error("Error fetching products:", error);
        } finally {
            setLoadingProducts(false);
        }
    };

    useEffect(() => {
        fetchProducts();
    }, [debouncedFilters.page, debouncedFilters.limit, debouncedFilters.search]);

    useEffect(() => {
        if (isEditMode && id) {
            const fetchAddonDetails = async () => {
                setLoadingDetails(true);
                try {
                    const response = await callApi<ISingleAddonResponse>(
                        `${API_URL + API_ENDPOINT.addons}/${id}`,
                        { method: 'GET' }
                    );
                    const addon = response?.data;

                    reset({
                        name: addon.name,
                        price: Number(addon.price),
                        productId: addon.productId,
                        isCounter: addon.isCounter,
                        inventory: addon.inventory || 0,
                        status: addon.status as 'active' | 'inactive',
                    });
                } catch (error) {
                    ThrowError(error);
                } finally {
                    setLoadingDetails(false);
                }
            };
            fetchAddonDetails();
        }
    }, [id, isEditMode, reset]);

    const onSubmit = async (data: IAddAddonForm) => {
        if (!data.productId) {
            showToastNotification("danger", "Please select a product first");
            return;
        }

        try {
            const url = isEditMode
                ? `${API_URL + API_ENDPOINT.addons}/${id}`
                : `${API_URL + API_ENDPOINT.addons}`;

            // Swagger shows POST for create. Assuming PATCH/PUT for update.
            const method = isEditMode ? 'PATCH' : 'POST';

            const payload = {
                productId: data.productId,
                name: data.name?.trim(),
                price: Number(data.price),
                isCounter: data.isCounter,
                inventory: Number(data.inventory),
                status: data.status,
            };

            const response = await callApi(url, {
                method,
                data: payload,
            });

            showToastNotification('success', response?.message || `Add-On ${isEditMode ? 'updated' : 'created'} successfully`);
            navigate(-1);
        } catch (error) {
            ThrowError(error);
        }
    };

    const columns: TableColumn<IProduct>[] = [
        {
            key: "select",
            header: "Select",
            render: (item) => (
                <input
                    type="radio"
                    name="selectedProduct"
                    checked={selectedProductId === item.id}
                    onChange={() => setValue("productId", item.id, { shouldValidate: true })}
                    className="w-4 h-4 text-primary focus:ring-primary border-gray-300"
                />
            )
        },
        {
            key: "image",
            header: "Product",
            render: (item) => (
                <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded bg-secondary/20 overflow-hidden flex-shrink-0 border border-secondary/10">
                        {item.media && item.media[0] ? (
                            <img
                                src={item.media[0].url.startsWith('http') ? item.media[0].url : `${API_URL}/${item.media[0].url}`}
                                alt={item.name}
                                className="w-full h-full object-cover"
                            />
                        ) : (
                            <div className="w-full h-full flex items-center justify-center text-[8px] text-muted-foreground">No Img</div>
                        )}
                    </div>
                    <span className="font-medium text-xs text-foreground line-clamp-1">{item.name}</span>
                </div>
            )
        },
        {
            key: "price",
            header: "Price",
            render: (item) => (
                <div className="font-semibold text-primary">
                    {formatRand(item.price)}
                </div>
            )
        },
    ];

    if (loadingDetails) {
        return (
            <Layout>
                <div className="flex items-center justify-center min-h-[400px]">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <MainHeading
                title={isEditMode ? "Edit Add-On" : "Add Add-Ons"}
                description="Create extra items and link them to products"
                showBack
                onBack={() => navigate(-1)}
            />

            <div className="mt-8 grid grid-cols-1 xl:grid-cols-12 gap-8 pb-20">
                {/* ── Left Column: Product Selection ── */}
                <div className="xl:col-span-7 space-y-6">
                    <div className="bg-card rounded-2xl border p-6 shadow-sm">
                        <div className="flex items-center justify-between mb-6">
                            <h3 className="text-sm font-bold text-foreground">1. Select Product</h3>
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                                <input
                                    type="text"
                                    placeholder="Search products..."
                                    className="h-8 pl-9 pr-4 rounded-md border bg-background text-xs w-48 focus:outline-none focus:ring-1 focus:ring-primary transition-all"
                                    value={filters.search}
                                    onChange={(e) => updateFilter({ search: e.target.value })}
                                />
                            </div>
                        </div>

                        <div className="min-h-[300px]">
                            <CommonTable
                                columns={columns}
                                data={products}
                                loading={loadingProducts}
                                emptyMessage="No products found."
                            />
                        </div>

                        <div className="mt-4">
                            <Pagination
                                currentPage={debouncedFilters.page || 1}
                                totalPages={pageMetadata.totalPages}
                                totalItems={pageMetadata.total}
                                itemsPerPage={pageMetadata.limit}
                                onPageChange={handlePageChange}
                            />
                        </div>
                    </div>
                </div>

                {/* ── Right Column: Add-On Form ── */}
                <div className="xl:col-span-5">
                    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                        <div className="bg-card rounded-2xl border p-8 shadow-sm">
                            <h3 className="text-sm font-bold text-foreground mb-10">2. Add-On Details</h3>

                            <div className="flex flex-col gap-6">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <FormField
                                        label="Addon Name"
                                        name="name"
                                        type="text"
                                        placeholder="e.g. Extra Cheese"
                                        error={errors.name?.message}
                                        {...register('name', { ...errorTypes.required('Add-On Name') })}
                                    />
                                    <FormField
                                        label="Addon Price"
                                        name="price"
                                        type="number"
                                        placeholder="0"
                                        error={errors.price?.message}
                                        min={0}
                                        {...register('price', { ...errorTypes.required('Price') })}
                                    />
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-1 gap-6">
                                    <FormField
                                        label="Inventory"
                                        name="inventory"
                                        type="number"
                                        placeholder="100"
                                        error={errors.inventory?.message}
                                        min={0}
                                        {...register('inventory', { ...errorTypes.required('Inventory') })}
                                    />
                                </div>

                                <div className="grid grid-cols-2 gap-6 pt-2">
                                    {/* <div className="flex flex-col gap-3">
                                        <label className="text-xs font-bold text-foreground">Counter</label>
                                        <Controller
                                            name="isCounter"
                                            control={control}
                                            render={({ field }) => (
                                                <div className="flex items-center gap-3 h-10">
                                                    <Switch
                                                        checked={field.value}
                                                        onCheckedChange={field.onChange}
                                                    />
                                                    <span className="text-[10px] font-bold text-muted-foreground uppercase">
                                                        {field.value ? 'YES' : 'NO'}
                                                    </span>
                                                </div>
                                            )}
                                        />
                                    </div> */}

                                    <div className="flex flex-col gap-3">
                                        <label className="text-xs font-bold text-foreground">Status</label>
                                        <Controller
                                            name="status"
                                            control={control}
                                            render={({ field }) => (
                                                <div className="flex items-center gap-3 h-10">
                                                    <Switch
                                                        checked={field.value === 'active'}
                                                        onCheckedChange={(checked) => field.onChange(checked ? 'active' : 'inactive')}
                                                    />
                                                    <StatusBadge status={field.value} />
                                                </div>
                                            )}
                                        />
                                    </div>
                                </div>
                            </div>

                            {errors.productId && (
                                <p className="mt-8 text-xs font-bold text-destructive animate-pulse bg-destructive/5 p-3 rounded-lg border border-destructive/20">
                                    ⚠️ Please select a product from the list on the left to link this add-on.
                                </p>
                            )}

                            <div className="flex flex-col gap-3 mt-12">
                                <Button
                                    type="submit"
                                    className="w-full h-11 shadow-lg shadow-primary/20"
                                    isLoading={isSubmitting}
                                >
                                    {isEditMode ? "Update Add-On" : "Create & Link Add-On"}
                                </Button>
                                <Button
                                    type="button"
                                    variant="outline"
                                    className="w-full h-11"
                                    onClick={() => navigate(-1)}
                                >
                                    Cancel
                                </Button>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </Layout>
    );
};
